<template>
    <div class="space-y-3">
        <!-- Loading State -->
        <Card v-if="loading" class="min-h-24 items-center justify-center px-4 text-sm text-muted-foreground">
            Loading activities...
        </Card>

        <!-- Empty State -->
        <EmptyState
            v-else-if="displayedActivities.length === 0"
            title="No activities yet"
            description=""
            class="min-h-24 py-6"
        />

        <!-- Activity List -->
        <div v-else class="space-y-3">
            <div
                v-for="activity in displayedActivities"
                :key="activity.id"
                class="flex items-start gap-3 rounded-2xl px-1 py-1.5"
            >
                <div class="relative flex shrink-0 flex-col items-center">
                    <span class="size-2.5 rounded-full bg-violet-500/65 dark:bg-violet-300/80" />
                    <span class="mt-1 h-10 w-px bg-border/55" />
                </div>
                <div class="flex min-w-0 flex-1 items-start gap-3">
                    <div class="mt-0.5 flex shrink-0">
                        <div
                            v-if="activity.userAvatar"
                            class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl border border-border/50 bg-background/72"
                        >
                            <img :src="activity.userAvatar" :alt="activity.userName" class="h-full w-full object-cover">
                        </div>
                        <div
                            v-else
                            class="flex h-9 w-9 items-center justify-center rounded-xl border border-border/50 bg-background/72 text-xs font-semibold text-violet-600 dark:text-violet-300"
                        >
                            {{ getInitials(activity.userName) }}
                        </div>
                    </div>

                    <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-start gap-2">
                            <p class="min-w-0 flex-1 text-sm font-medium text-foreground">
                                {{ buildTitle(activity) }}
                            </p>
                            <Badge :variant="getActionVariant(activity.action)">
                                {{ getActionLabel(activity.action) }}
                            </Badge>
                        </div>

                        <p class="mt-1 text-xs text-muted-foreground">
                            {{ buildSubtitle(activity) }}
                        </p>

                        <p
                            v-if="activity.previousValue && activity.newValue"
                            class="mt-1 text-xs text-muted-foreground"
                        >
                            {{ summarizeDiff(activity.previousValue, activity.newValue) }}
                        </p>

                        <p class="mt-1 text-xs text-muted-foreground">
                            {{ formatRelativeTime(activity.createdAt) }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Load More Button -->
            <div v-if="canLoadMore" class="text-center pt-4">
                <Button 
                    variant="outline" 
                    size="sm"
                    @click="loadMore"
                    :disabled="loading"
                >
                    Load More
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
/**
 * ActivityFeed Component
 * 
 * Displays a chronological feed of activities (create, update, delete, etc.)
 * Provides visibility into what's happening in the workspace.
 * 
 * Features:
 * - Activity timeline with user avatars
 * - Action type badges (create, update, delete, etc.)
 * - Field-level change tracking for updates
 * - Relative time formatting (e.g., "2 hours ago")
 * - Pagination support with "Load More"
 * - Filtering by entity type (optional)
 * - Visual distinction for different actions
 * 
 * Design Patterns:
 * - Immutable activities (created once, never modified)
 * - User avatar display with fallback to initials
 * - Color-coded action badges for quick scanning
 * - Chronological order (newest first)
 * - Accessible time formatting
 * 
 * Props:
 * - entityType: Optional filter by entity type (task, project, client, team)
 * - entityId: Optional filter by specific entity ID
 * - maxItems: Number of items to load initially (default 20)
 * - loadMoreCount: Number of items per "Load More" (default 10)
 * 
 * Usage:
 * // Recent activities (all)
 * <ActivityFeed />
 * 
 * // Task-specific activities
 * <ActivityFeed entity-type="task" :entity-id="taskId" />
 * 
 * // Custom load count
 * <ActivityFeed :max-items="50" :load-more-count="15" />
 * 
 * Future Enhancements:
 * - Real-time updates via WebSocket
 * - Activity grouping by time period (Today, Yesterday, This Week)
 * - User mentions/notifications in activity text
 * - Activity comments/threads
 * - Filtering by action type
 * - Export/archive activities
 * 
 * @example Integration with Dashboard
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Recent Activity</CardTitle>
 *   </CardHeader>
 *   <CardContent>
 *     <ActivityFeed :max-items="10" />
 *   </CardContent>
 * </Card>
 */
import { computed, ref, onMounted } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Activity } from '~/types/models';

interface Props {
    /** Optional: Filter by entity type (task, project, client, team) */
    entityType?: Activity['entityType'];
    /** Optional: Filter by specific entity ID */
    entityId?: string;
    /** Optional: Filter by user ID */
    userId?: string;
    /** Number of items to load initially */
    maxItems?: number;
    /** Number of items per "Load More" */
    loadMoreCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
    maxItems: 20,
    loadMoreCount: 10
});

// ========================================================================
// STATE
// ========================================================================

const loading = ref(true);
const allActivities = ref<Activity[]>([]);
const displayedCount = ref(props.maxItems);

// ========================================================================
// STORES
// ========================================================================

const activityStore = useActivityStore();

// ========================================================================
// LIFECYCLE
// ========================================================================

/**
 * Load activities on component mount
 * Uses appropriate filter based on props
 */
onMounted(async () => {
    loading.value = true;
    try {
        if (props.entityId && props.entityType) {
            // Load activities for specific entity
            allActivities.value = await activityStore.fetchActivitiesByEntity(
                props.entityType,
                props.entityId,
                100
            );
        } else if (props.userId) {
            // Load activities by user
            allActivities.value = await activityStore.fetchActivitiesByUser(
                props.userId,
                100
            );
        } else {
            // Load recent activities
            allActivities.value = await activityStore.fetchRecentActivities(100);
        }
    } catch (err) {
        console.error('Failed to load activities:', err);
    } finally {
        loading.value = false;
    }
});

// ========================================================================
// COMPUTED PROPERTIES
// ========================================================================

/**
 * Get activities to display based on pagination
 */
const displayedActivities = computed(() => {
    return allActivities.value.slice(0, displayedCount.value);
});

function buildTitle(activity: Activity): string {
    const actor = activity.userName || 'Unknown';
    const verb = getActionVerb(activity.action);
    const description = String(activity.description || '').trim();
    if (!description) return `${actor} ${verb}`;
    return `${actor} ${verb} ${description.charAt(0).toLowerCase()}${description.slice(1)}`;
}

function buildSubtitle(activity: Activity): string {
    const entity = activity.entityType ? `${activity.entityType} activity` : 'Workspace activity';
    return entity.charAt(0).toUpperCase() + entity.slice(1);
}

function summarizeDiff(previousValue: string, newValue: string): string {
    const prev = String(previousValue || '').trim();
    const next = String(newValue || '').trim();
    if (!prev || !next) return '';
    return `${truncateValue(prev)} -> ${truncateValue(next)}`;
}

function truncateValue(value: string): string {
    return value.length > 72 ? `${value.slice(0, 72)}…` : value;
}

/**
 * Check if there are more activities to load
 */
const canLoadMore = computed(() => {
    return displayedCount.value < allActivities.value.length;
});

// ========================================================================
// METHODS
// ========================================================================

/**
 * Load more activities
 */
function loadMore() {
    displayedCount.value += props.loadMoreCount;
}

/**
 * Get initials from user name for avatar fallback
 * 
 * @param name - User name
 * @returns Two-character initials
 */
function getInitials(name?: string): string {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
}

/**
 * Get human-readable verb for action
 * 
 * @param action - Action type
 * @returns Verb phrase
 */
function getActionVerb(action: Activity['action']): string {
    const verbs: Record<Activity['action'], string> = {
        'create': 'created',
        'update': 'updated',
        'delete': 'deleted',
        'comment': 'commented on',
        'status_change': 'changed status of',
        'assign': 'assigned'
    };
    return verbs[action] || action;
}

/**
 * Get label for action badge
 * 
 * @param action - Action type
 * @returns Badge label
 */
function getActionLabel(action: Activity['action']): string {
    const labels: Record<Activity['action'], string> = {
        'create': 'Created',
        'update': 'Updated',
        'delete': 'Deleted',
        'comment': 'Commented',
        'status_change': 'Status',
        'assign': 'Assigned'
    };
    return labels[action] || action;
}

/**
 * Get badge variant for action
 * Different colors for different action types
 * 
 * @param action - Action type
 * @returns Badge variant
 */
function getActionVariant(action: Activity['action']): string {
    switch (action) {
        case 'delete':
            return 'destructive';
        case 'create':
            return 'default';
        case 'status_change':
            return 'secondary';
        case 'assign':
        case 'update':
        case 'comment':
        default:
            return 'outline';
    }
}

/**
 * Format time relative to now
 * Shows "2 hours ago", "1 day ago", etc.
 * 
 * @param dateValue - ISO date string or Date object
 * @returns Relative time string
 */
function formatRelativeTime(dateValue?: unknown): string {
    if (!dateValue) return 'Never';

    try {
        let time: number;
        
        if (typeof dateValue === 'string') {
            time = new Date(dateValue).getTime();
        } else if (dateValue instanceof Date) {
            time = dateValue.getTime();
        } else if (typeof dateValue === 'number') {
            time = dateValue;
        } else {
            // Firestore timestamp object
            time = (dateValue as any).toDate?.().getTime?.();
        }

        if (!time) return 'Unknown';

        const now = Date.now();
        const diff = now - time;

        // Minutes
        if (diff < 60000) return 'just now';

        // Hours
        if (diff < 3600000) {
            const mins = Math.floor(diff / 60000);
            return `${mins}m ago`;
        }

        // Days
        if (diff < 86400000) {
            const hours = Math.floor(diff / 3600000);
            return `${hours}h ago`;
        }

        // Weeks
        if (diff < 604800000) {
            const days = Math.floor(diff / 86400000);
            return `${days}d ago`;
        }

        // Months
        const weeks = Math.floor(diff / 604800000);
        return `${weeks}w ago`;
    } catch {
        return 'Unknown';
    }
}
</script>
